import { regionFormat } from '@/lib/region-format';

// The public booking page formats in the deployment's locale, which it reads
// from its own context response: there is no session to load the region config.

type WeekInfo = { firstDay: number };

/** 0 = Sunday … 6 = Saturday: the day the locale's weeks start on. */
export function firstDayOfWeek(locale: string): number {
  // getWeekInfo() is the current spelling, weekInfo the Safari 17 one; Firefox
  // has neither, and there the US starts on Sunday and everywhere else this
  // CRM sells on Monday.
  const intlLocale = new Intl.Locale(locale) as Intl.Locale & { getWeekInfo?: () => WeekInfo; weekInfo?: WeekInfo };
  const info = intlLocale.getWeekInfo?.() ?? intlLocale.weekInfo;
  const firstDay = info ? info.firstDay : intlLocale.maximize().region === 'US' ? 7 : 1;
  return firstDay % 7;
}

/** The grid's column heads, one letter per weekday from `firstDay`. */
export function weekdayInitials(locale: string, firstDay: number): string[] {
  // 2024-01-07 is a Sunday.
  return Array.from({ length: 7 }, (_, index) =>
    regionFormat.formatDate(new Date(Date.UTC(2024, 0, 7 + ((firstDay + index) % 7))), {
      locale,
      weekday: 'narrow',
      timeZone: 'UTC',
    })
  );
}

/** Month grid of "YYYY-MM-DD" keys starting on `firstDay` (null = padding cell). */
export function monthGrid(yearMonth: string, firstDay: number): (string | null)[][] {
  const [year, month] = yearMonth.split('-').map(Number);
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells: (string | null)[] = Array.from({ length: (first.getUTCDay() - firstDay + 7) % 7 }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${yearMonth}-${String(day).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  const weeks: (string | null)[][] = [];
  for (let index = 0; index < cells.length; index += 7) {
    weeks.push(cells.slice(index, index + 7));
  }
  return weeks;
}

export function monthLabel(yearMonth: string, locale: string): string {
  const [year, month] = yearMonth.split('-').map(Number);
  return regionFormat.formatDate(new Date(Date.UTC(year, month - 1, 1)), { locale, month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** A slot's "HH:MM" (24h, from the slot engine) in the locale's clock: "2:30 PM" / "14:30". */
export function slotTimeLabel(localTime: string, locale: string): string {
  const [hour, minute] = localTime.split(':').map(Number);
  return regionFormat.formatTime(new Date(Date.UTC(2000, 0, 1, hour, minute)), { locale, timeStyle: 'short', timeZone: 'UTC' });
}

export function formatConfirmedStart(startAt: string, timezone: string, locale: string): string {
  try {
    return regionFormat.formatDateTime(startAt, {
      locale,
      timeZone: timezone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return startAt;
  }
}
